import React from 'react';
import HeaderNavbar from '../components/HeaderNavbar';
import Footer from '../components/Footer';
import config from '../config';
import './HomeNext.css';

const landingThemes = ['nebula', 'aurora', 'iris', 'ultraviolet', 'amethyst'];

const makerBenefits = [
  'Step-by-step guides with annotated images and color-coded text that clearly points to the exact part of the photo being discussed.',
  'Structured BOMs with categories, images, quantities, modules, and part details that stay usable instead of turning into a flat parts dump.',
  'Dynamic guide references to BOM items, so the parts mentioned in the instructions are always easy to identify.',
  'Comments on each step or BOM item for troubleshooting, shared fixes, and community knowledge.',
  'The whole project in one place: overview, guides, BOMs, exports, and progress.',
  'Guide export to PDF plus BOM export to PDF, CSV, and JSON.',
  'Progress tracking so you can stop, resume, and see exactly what is already done.'
];

const creatorBenefits = [
  'Multi-user organizations for teams that manage multiple projects, guides, and BOMs together.',
  'An editor built around the published result, so creators work in the same structure makers will follow.',
  'A method that pushes guide authors to think step by step, prioritize annotated images, and write around the build sequence instead of vague explanations.',
  'Remote photo upload by QR-linked mobile device while the main editor stays open on desktop.',
  'Inline image annotation plus crop, rotation, and resize tools without leaving the guide.',
  'AI-assisted step drafting from a strong title and step images.',
  'Markdown with hardware-specific structure: custom bullets, alerts, hints, !guide, !item, and !bom references.',
  'Draft publishing flow, autosave, history with rollback, internal notes/TODOs, step videos, moderation-ready comments, and Markdown export with images.',
  'Import your current BOMs from anywhere in any format.'
];

const methodSteps = [
  {
    eyebrow: '1. Structure the build',
    title: 'Break hardware assembly into steps that are meant to be followed.',
    text: 'StepWiseDocs is not a blank page. It gives creators a specific structure: ordered steps, scoped instructions, visual evidence, and a clear relationship between each action and the parts involved.'
  },
  {
    eyebrow: '2. Anchor the explanation to the image',
    title: 'Use annotated media so the instruction and the photo say the same thing.',
    text: 'The method makes images do real work. Arrows, labels, and color-coded callouts remove the common open-hardware problem of text that leaves makers guessing what they should actually touch or inspect.'
  },
  {
    eyebrow: '3. Connect steps, BOMs, and project context',
    title: 'Publish documentation as one linked system, not scattered files.',
    text: 'Guides, BOMs, project metadata, comments, exports, and progress tracking live together. The result is documentation that stays consistent for creators and far easier to follow for makers.'
  }
];

const showcaseCards = [
  {
    title: 'Image-anchored instructions',
    body: 'Makers see the step, the image, and the supporting build context together. That is the core StepWiseDocs advantage: fewer blind spots, less guesswork, and less interpretation.',
    mediaType: 'image',
    mediaSrc: '/images/landing/guide-overview.png',
    mediaAlt: 'StepWiseDocs guide page with assembly guide and linked project context'
  },
  {
    title: 'Structured BOMs, not loose parts lists',
    body: 'Bills of materials stay useful because they support categories, modules, images, versions, and exports. BOMs are documentation objects in their own right.',
    mediaType: 'image',
    mediaSrc: '/images/landing/bom-overview.png',
    mediaAlt: 'StepWiseDocs BOM page showing structured bill of materials information'
  },
  {
    title: 'Projects as complete build systems',
    body: 'Each project can group multiple guides and BOMs in one place so makers can navigate from overview to parts to assembly without context switching.',
    mediaType: 'image',
    mediaSrc: '/images/landing/project-overview.png',
    mediaAlt: 'StepWiseDocs project page listing guides and bills of materials'
  },

  {
    title: 'AI-assisted step drafting',
    body: 'Give a step a strong title, add the relevant images, and StepWiseDocs can draft the first pass of the instructional text for you. It speeds up documentation work without replacing the creator’s technical judgment, which is exactly the right balance for production-quality guides.',
    mediaType: 'video',
    mediaSrc: '/images/landing/ai.mp4'
  },
   {
    title: 'Edit once, publish what makers will actually use',
    body: 'The editing workflow is built for creators who need to assemble real hardware documentation quickly while keeping the output consistent.',
    mediaType: 'video',
    mediaSrc: '/images/landing/editor-flow.mp4',
    posterSrc: '/images/landing/guide-overview.png'
  },
  {
    title: 'Link the exact part in the exact step with !item',
    body: 'Creators can reference BOM items directly inside guide text with !item, so the instruction stays tied to the specific part being used. That removes ambiguity for makers and keeps the narrative, the parts list, and the build sequence synchronized.',
    mediaType: 'video',
    mediaSrc: '/images/landing/item.mp4',
    mediaHint: 'Show !item references inside a step and the linked BOM item context'
  },
  {
    title: 'Remote photo capture by QR link',
    body: 'Open the guide editor on desktop, scan the QR code from another device, and upload photos directly into the step you are editing. The remote uploader stays tied to the live editing session, so images appear in the active draft instead of getting lost in a separate upload flow.',
    mediaType: 'image',
    mediaSrc: '/images/landing/qr.png',
    mediaAlt: 'StepWiseDocs QR-linked photo upload flow showing phone and desktop interfaces'
  },
  {
    title: 'Portable outputs when you need them',
    body: 'Creators can export guides to Markdown with images, and makers can export guides and BOMs to PDF. BOMs also export to CSV and JSON for downstream workflows.',
    mediaType: 'image',
    mediaSrc: '/images/landing/takeaway.png',
    mediaAlt: 'StepWiseDocs export options showing PDF, CSV, JSON, and Markdown outputs'
  }
];

function FeatureMedia({ card }) {
  if (card.mediaType === 'video') {
    return (
      <video
        className="landing-next-media"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        poster={card.posterSrc}
      >
        <source src={card.mediaSrc} type="video/mp4" />
      </video>
    );
  }

  if (card.mediaType === 'diagram') {
    return (
      <div className="landing-next-qr-card" aria-hidden="true">
        <div className="landing-next-qr-device landing-next-qr-device-phone">
          <div className="landing-next-qr-screen">
            <div className="landing-next-qr-grid" />
          </div>
        </div>
        <div className="landing-next-qr-flow">
          <span>Scan</span>
          <span>Capture</span>
          <span>Sync</span>
        </div>
        <div className="landing-next-qr-device landing-next-qr-device-desktop">
          <div className="landing-next-qr-screen">
            <div className="landing-next-qr-step">Step 6</div>
            <div className="landing-next-qr-thumb" />
            <div className="landing-next-qr-thumb is-accent" />
          </div>
        </div>
      </div>
    );
  }

  if (card.mediaType === 'placeholder') {
    return (
      <div className="landing-next-placeholder-card" aria-hidden="true">
        <div className="landing-next-placeholder-badge">{card.mediaLabel}</div>
        <div className="landing-next-placeholder-window">
          <div className="landing-next-placeholder-toolbar">
            <span />
            <span />
            <span />
          </div>
          <div className="landing-next-placeholder-panel">
            <div className="landing-next-placeholder-line is-wide" />
            <div className="landing-next-placeholder-line" />
            <div className="landing-next-placeholder-line is-short" />
          </div>
          <div className="landing-next-placeholder-panel is-accent">
            <div className="landing-next-placeholder-line is-short" />
            <div className="landing-next-placeholder-line is-wide" />
            <div className="landing-next-placeholder-line" />
          </div>
        </div>
        <div className="landing-next-placeholder-hint">{card.mediaHint}</div>
      </div>
    );
  }

  return (
    <img
      className="landing-next-media"
      src={card.mediaSrc}
      alt={card.mediaAlt}
      loading="lazy"
    />
  );
}

function HomeNext() {
  const siteName = config.siteName || 'StepWiseDocs';
  const rawLandingTheme = (config.landingTheme || '').toLowerCase();
  const landingTheme = landingThemes.includes(rawLandingTheme) ? rawLandingTheme : 'nebula';

  return (
    <>
      <HeaderNavbar />

      <main className={`landing-next landing-next-theme-${landingTheme}`}>
        <section className="landing-next-hero">
          <div className="landing-next-hero-copy">
            <span className="landing-next-eyebrow">Open hardware documentation, standardized</span>
            <h1 className="landing-next-title">
              {siteName} is the documentation method for open hardware.
            </h1>
            <p className="landing-next-lead">
              StepWiseDocs gives creators a structured way to document builds: step-by-step thinking, image-first explanations, and BOM-linked instructions. The result is better documentation for creators and much clearer builds for makers.
            </p>
            <div className="landing-next-hero-actions">
              <a href={`${config.prefixPath}/projects`} className="btn btn-dark btn-lg">
                Browse projects
              </a>
              <a href={`${config.prefixPath}/publish`} className="btn btn-outline-dark btn-lg">
                Create your Project
              </a>
            </div>
            <div className="landing-next-hero-note">Public projects are browsable without an account.</div>
          </div>

          <div className="landing-next-hero-panel">
            <div className="landing-next-panel-kicker">The core promise</div>
            <div className="landing-next-panel-quote">
              A creator may be excellent at designing hardware and still produce confusing documentation. StepWiseDocs closes that gap by enforcing a method.
            </div>
            <div className="landing-next-pill-grid">
              <span className="landing-next-pill">Step-by-step structure</span>
              <span className="landing-next-pill">Annotated images</span>
              <span className="landing-next-pill">BOM-linked instructions</span>
              <span className="landing-next-pill">Reusable exports</span>
              <span className="landing-next-pill">Comments and progress</span>
               <span className="landing-next-pill">AI-Assisted creation</span>
              <span className="landing-next-pill">Draft-to-publish workflow</span>
             
            </div>
          </div>
        </section>

        <section className="landing-next-method" id="get-started">
          <div className="landing-next-section-head">
            <span className="landing-next-eyebrow">Why StepWiseDocs exists</span>
            <h2>The goal is not prettier markdown. The goal is reliable build documentation.</h2>
            <p>
              StepWiseDocs creates a standard for documenting open hardware so makers can follow builds with confidence and creators can publish documentation that is clear by design.
            </p>
          </div>

          <div className="landing-next-method-grid">
            {methodSteps.map((step) => (
              <article key={step.title} className="landing-next-method-card">
                <div className="landing-next-method-eyebrow">{step.eyebrow}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-next-audiences">
          <div className="landing-next-section-head landing-next-section-head-is-balanced">
            <span className="landing-next-eyebrow">Two clear outcomes</span>
            <h2>Why makers need it. Why creators choose it.</h2>
          </div>

          <div className="landing-next-audience-grid">
            <article className="landing-next-audience-card is-makers">
              <div className="landing-next-audience-label">For Makers</div>
              <h3>Build with less ambiguity.</h3>
              <p>
                Makers benefit from the method because the documentation is constrained to be clearer: annotated images, better-scoped steps, explicit part references, and fewer unexplained jumps.
              </p>
              <ul>
                {makerBenefits.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>

            <article className="landing-next-audience-card is-creators">
              <div className="landing-next-audience-label">For Creators</div>
              <h3>Publish documentation with a system, not with improvisation.</h3>
              <p>
                StepWiseDocs gives creators a production-ready workflow for open hardware documentation: one that supports collaboration, structured authoring, review, exports, and community feedback.
              </p>
              <ul>
                {creatorBenefits.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className="landing-next-showcase">
          <div className="landing-next-section-head landing-next-section-head-is-balanced">
            <span className="landing-next-eyebrow">Feature showcase</span>
            <h2>Main product capabilities, shown in context.</h2>
          </div>

          <div className="landing-next-showcase-grid">
            {showcaseCards.map((card) => (
              <article key={card.title} className="landing-next-showcase-card">
                <FeatureMedia card={card} />
                <div className="landing-next-showcase-copy">
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-next-cta">
          <div className="landing-next-cta-copy">
            <span className="landing-next-eyebrow">Ready to use the method?</span>
            <h2>Explore public projects or start documenting your own hardware builds.</h2>
            <p>
              StepWiseDocs is built for teams and makers who need hardware documentation to be repeatable, understandable, and maintainable.
            </p>
          </div>
          <div className="landing-next-hero-actions">
            <a href={`${config.prefixPath}/projects`} className="btn btn-dark btn-lg">
              Browse projects
            </a>
            <a href={`${config.prefixPath}/publish`} className="btn btn-outline-dark btn-lg">
              Create your Project
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default HomeNext;
